import type { DatoxSessionContext } from "./api"
import { getWorkspace, listWorkspaces, workspacePath, type FactsheetWorkspace } from "./workspaces"
import { getWorkflowApplication } from "./automations"

type WorkflowReportScope = { projectID: string; periodID: string; caseID?: string }

export function workflowReportChoices(reports: FactsheetWorkspace[], scope: WorkflowReportScope, runID?: string) {
  const scoped = reports.filter((report) => report.project_id === scope.projectID && report.period_id === scope.periodID &&
    (report.case_id ?? null) === (scope.caseID ?? null) && !["archived", "deleted"].includes(report.status))
  const associated = runID ? scoped.filter((report) => report.runs.some((run) => run.id === runID)) : []
  return {
    direct: associated.length === 1 || (!runID && scoped.length === 1),
    reports: (associated.length === 1 ? associated : scoped).map((report) => ({ name: report.name, href: workspacePath(report) })),
  }
}

export async function resolveWorkflowReports(scope: WorkflowReportScope) {
  const [reports, application] = await Promise.all([
    listWorkspaces(), getWorkflowApplication(scope.projectID, scope.periodID, scope.caseID).catch(() => undefined),
  ])
  return workflowReportChoices(reports, scope, application?.latest_run?.id)
}

export async function resolveSessionReport(context: DatoxSessionContext) {
  // New APIs resolve the durable association directly, including older runs.
  if ("report" in context) return context.report ?? undefined
  // Support running APIs during rollout, using only exact saved associations.
  const scope = context.session_metadata.report_chat_scope as { workspace_id?: string; dashboard_id?: string; target_id?: string } | undefined
  const runID = context.session_metadata.automation_run_id
  const owner = scope?.workspace_id || (!scope?.dashboard_id ? context.session_metadata.report_workspace_id : undefined)
  if (!runID && typeof owner === "string") {
    const report = await getWorkspace(context.project_id, owner)
    if (report.project_id !== context.project_id || report.period_id !== (context.period_id ?? null)) return
    const dashboard = report.dashboards.find((item) => item.id === scope?.dashboard_id)
    return { name: report.name, workspace_id: report.id, dashboard_id: dashboard?.id,
      target_id: report.config.targets.find((item) => item.id === scope?.target_id)?.id }
  }
  if (typeof runID !== "string" && !scope?.dashboard_id) return
  const reports = (await listWorkspaces()).filter((report) => report.project_id === context.project_id && report.period_id === (context.period_id ?? null))
  const report = reports.find((item) => typeof runID === "string"
    ? item.runs.some((run) => run.id === runID && run.session_id === context.session_id)
    : item.legacy_dashboard_id === scope?.dashboard_id)
  if (!report) return
  return { name: report.name, workspace_id: report.legacy_dashboard_id ? undefined : report.id,
    dashboard_id: report.legacy_dashboard_id, target_id: undefined }
}

export function sessionReportPath(projectID: string, report: NonNullable<DatoxSessionContext["report"]>) {
  if (report.workspace_id) {
    const query = new URLSearchParams()
    if (report.target_id) query.set("target", report.target_id)
    if (report.dashboard_id) query.set("dashboard", report.dashboard_id)
    return `/reports/${encodeURIComponent(projectID)}/${encodeURIComponent(report.workspace_id)}${query.size ? `?${query}` : ""}`
  }
  if (report.dashboard_id) return `/reports/legacy/${encodeURIComponent(projectID)}/${encodeURIComponent(report.dashboard_id)}`
}
